"use client";

import { useEffect, useRef } from "react";
import { bfcache_restore_action } from "@core/helpers/bfcache-rules";
import { visitor_state_snapshot } from "@core/helpers/client/visitor-state";

// Back/forward cache (all sites, asked by jument): pages are no longer `no-store` (see
// `@core/helpers/nextjs/page-cache-rules`), so Back/Forward can show a page exactly as it was left, from
// memory: React state, loading flags and fetched data come back and no effect runs again. The only signal
// is `pageshow` with `event.persisted === true`. These helpers are the one place to react to it.

let left_at = 0;
let left_state = null;

if (typeof window !== "undefined") {
	// what the page looked like when it was left; compared on restore by `useBfcacheGuard`
	window.addEventListener("pagehide", () => {
		left_at = Date.now();
		left_state = visitor_state_snapshot();
	});
}

// Calls `callback(event, { frozen_ms })` each time the page comes back from the bfcache. `frozen_ms` is how
// long it was frozen (timers did not tick meanwhile). Returns an unsubscribe function.
export const on_bfcache_restore = (callback) => {
	if (typeof window === "undefined") return () => {};
	const handler = (event) => {
		if (!event?.persisted) return;
		callback(event, { frozen_ms: left_at ? Math.max(0, Date.now() - left_at) : 0 });
	};
	window.addEventListener("pageshow", handler);
	return () => window.removeEventListener("pageshow", handler);
};

// Hook form of `on_bfcache_restore`. The latest callback is used, so it may read current state.
export const useBfcacheRestore = (callback) => {
	const callback_ref = useRef(callback);
	callback_ref.current = callback;
	useEffect(() => on_bfcache_restore((event, info) => callback_ref.current?.(event, info)), []);
};

// For a page that must never be shown from memory: a restore always reloads it.
export const useReloadOnBfcacheRestore = (enabled = true) => {
	useBfcacheRestore(() => {
		if (enabled) window.location.reload();
	});
};

// Mounted once, in AuthContextProvider (so on every site). A restored page is reloaded when the visitor
// state changed while it was away (see `bfcache_restore_action`); otherwise it stays as it was, instantly.
export const useBfcacheGuard = () => {
	useBfcacheRestore(() => {
		if (!left_state) return;
		if (bfcache_restore_action(left_state, visitor_state_snapshot()) === "reload") {
			window.location.reload();
		}
	});
};

export { bfcache_restore_action };
